import ToggleOptions from "components/Common/ToggleOptions";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { useAppSelector } from "hooks/hook";
import { keys, map, orderBy, startCase, values } from "lodash";
import PropTypes from "prop-types";
import TreeSelect, { SHOW_PARENT } from "rc-tree-select";
import { useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useDispatch } from "react-redux";
import { Button, Col, Row, Spinner } from "reactstrap";
import { Table } from "resbutler-ui/components/WizElements/Elements";
import { catchExceptionCallback } from "resbutler-ui/error";
import { decodeRoleLabel } from "resbutler-utils/roles";
import { setFilterPrinterIds, setOperationMode } from "../../store/reducers/rootSlice";
import { changeSetting, toggleColourAlerts, toggleSetting, updateColourAlertRange } from "../../store/reducers/settingsSlice";
import useUrlParams from "./../../hooks/useUrlParams";
import { LogOut } from "lucide-react";

export enum OperationMode {
  AllStep,
  OneStep,
}

const Offsidebar = () => {
  const { settings, operationMode, filterPrinterIds, printers, restaurants, user } = useAppSelector(
    (state) => ({
      settings: state.settings,
      operationMode: state.root.operationMode,
      filterPrinterIds: state.root.filterPrinterIds,
      printers: state.root.printers,
      restaurants: state.root.restaurants,
      user: state.root.user,
    }),
    shallowEqual
  );

  const { initials, displayName, email, role } = useMemo(() => {
    const name: string = user?.displayName || "Account";
    const tokens = name.split(/\s+/).filter(Boolean);
    let inits = (tokens[0]?.[0] || "") + (tokens[1]?.[0] || "");
    if (!inits) inits = name.slice(0, 2);
    return { initials: inits.toUpperCase() || "??", displayName: name, email: user?.email || "", role: decodeRoleLabel(user?.customClaims?.roles ?? 0) };
  }, [user]);
  const [restaurantId] = useUrlParams();
  const [offsidebarReady, setOffsidebarReady] = useState<boolean>(false);
  const asideRef = useRef<HTMLElement>(null);
  const [loadingLogout, setLoadingLogout] = useState<boolean>(false);
  const dispatch = useDispatch();
  function logout() {
    setLoadingLogout(true);
    firebase
      .auth()
      .signOut()
      .then(() => {
        setLoadingLogout(false);
        window.location.href = "/login";
      })
      .catch((error) => {
        catchExceptionCallback(error);
        setLoadingLogout(false);
      });
  }

  useEffect(() => {
    setOffsidebarReady(true);
  }, [restaurantId, settings.offsidebarOpen]);

  // close when clicking anywhere outside the sidebar
  useEffect(() => {
    if (!settings.offsidebarOpen) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Element;
      if (!(target instanceof Element)) return;
      if (asideRef.current?.contains(target)) return;
      // header toggle button handles itself; tree-select dropdown is portalled to body
      if (target.closest("[data-offsidebar-toggle], .rc-tree-select-dropdown")) return;
      dispatch(changeSetting({ name: "offsidebarOpen", value: false }));
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [settings.offsidebarOpen]);

  const closeSidebar = (e: any) => {
    if (e.target.classList.contains("offsidebar")) {
      dispatch(toggleSetting("offsidebarOpen"));
    }
  };

  const displayDocketTypesMap = [
    {
      label: "Pickup",
      isChecked: () => {
        return settings["showPickupDockets"];
      },
      onChecked: (checked) => {
        dispatch(changeSetting({ value: checked, name: "showPickupDockets" }));
      },
    },
    {
      label: "Counter Service",
      isChecked: () => {
        return settings["showCounterServiceDockets"];
      },
      onChecked: (checked) => {
        dispatch(changeSetting({ name: "showCounterServiceDockets", value: checked }));
      },
    },
    {
      label: "Table Service",
      isChecked: () => {
        return settings["showTableServiceDockets"];
      },
      onChecked: (checked) => {
        dispatch(changeSetting({ name: "showTableServiceDockets", value: checked }));
      },
    },
  ];

  const displayPreferenceMap = [
    {
      label: "Two step display (Prep & Serve)",
      isChecked: () => {
        return operationMode === OperationMode.AllStep;
      },
      onChecked: (checked) => {
        const mode = checked ? OperationMode.AllStep : OperationMode.OneStep;
        dispatch(setOperationMode(mode));
      },
    },
    {
      label: "Append new items into one docket",
      isChecked: () => {
        return settings["singleDocketPerTable"];
      },
      onChecked: (checked) => {
        dispatch(changeSetting({ name: "singleDocketPerTable", value: checked }));
      },
    }, //
    {
      label: "Hide customer name",
      isChecked: () => {
        return settings["showCustomerName"] !== true;
      },
      onChecked: (checked) => {
        dispatch(changeSetting({ name: "showCustomerName", value: !checked }));
      },
    }, //
    {
      label: "Group items by table",
      isChecked: () => {
        return settings["splitItemsByTable"];
      },
      onChecked: (checked) => {
        dispatch(changeSetting({ name: "splitItemsByTable", value: checked }));
      },
    },
    {
      label: "Group items by product enanchements",
      isChecked: () => {
        return settings["useGroupedDocketLayout"] ?? restaurants?.[restaurantId]?.useGroupedDocketLayout ?? false;
      },
      onChecked: (checked) => {
        dispatch(changeSetting({ name: "useGroupedDocketLayout", value: checked }));
      },
    }, //
  ];

  const printerTreeData = useMemo(
    () =>
      orderBy(
        keys(printers).map((key) => ({
          key,
          title: printers[key].name,
          value: key,
        })),
        (p) => p.title?.toLowerCase(),
        "asc"
      ),
    [printers]
  );

  const allPrinterIds = useMemo(() => printerTreeData.map((p) => p.value), [printerTreeData]);
  const selectedCount = filterPrinterIds?.length ?? 0;
  const allSelected = selectedCount > 0 && selectedCount === allPrinterIds.length;

  const renderPrinters = () => {
    return (
      <>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <small className="text-muted">{selectedCount === 0 ? "No printers selected (showing all)" : `${selectedCount} of ${allPrinterIds.length} selected`}</small>
          <div className="d-flex gap-2">
            <Button type="button" color="link" size="sm" className="p-0 text-decoration-none" disabled={allSelected} onClick={() => dispatch(setFilterPrinterIds(allPrinterIds))}>
              Select all
            </Button>
            <span className="text-muted">|</span>
            <Button type="button" color="link" size="sm" className="p-0 text-decoration-none" disabled={selectedCount === 0} onClick={() => dispatch(setFilterPrinterIds([]))}>
              Clear
            </Button>
          </div>
        </div>
        <TreeSelect
          className="form-control tree-select"
          choiceTransitionName="rc-tree-select-selection__choice-zoom"
          dropdownStyle={{ maxHeight: 320, overflow: "auto", zIndex: 1100 }}
          dropdownPopupAlign={{ overflow: { adjustY: 0, adjustX: 0 }, offset: [0, 2] }}
          treeLine
          value={filterPrinterIds}
          placeholder="Select printers…"
          showIcon={false}
          maxTagTextLength={25}
          autoClearSearchValue
          treeData={printerTreeData}
          treeCheckable
          showSearch
          treeNodeFilterProp="title"
          allowClear
          showCheckedStrategy={SHOW_PARENT}
          maxTagCount={3}
          onChange={(value) => {
            dispatch(setFilterPrinterIds(value));
          }}
          maxTagPlaceholder={(valueList) => {
            return `+${valueList.length} more`;
          }}
        />
      </>
    );
  };

  return offsidebarReady ? (
    <aside ref={asideRef} className="offsidebar" onClick={closeSidebar}>
      <div className="aside-inner">
        {/* START Off Sidebar (right) */}
        <div className="mt-4 ps-2 pe-2">
          <Row className="align-content-center mb-4">
            <div className="fw-bold mb-2 fs-6">Printer Filters</div>
            <div className="w-100">{renderPrinters()}</div>
          </Row>

          <hr className="border" />

          <Row className="align-content-center">
            <div className="fw-bold mb-2 fs-6">Display Docket Types</div>
            <>
              {displayDocketTypesMap.map((setting, ind) => {
                return (
                  <div key={`display-docket-type-checkbox-${ind}`} className="my-1">
                    <div className="checkbox c-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={setting.isChecked()}
                          onChange={(e) => {
                            setting.onChecked(e.target.checked);
                          }}
                        />
                        {setting.label}
                      </label>
                    </div>
                  </div>
                );
              })}
            </>
          </Row>

          <ToggleOptions className="mt-2" onClick={(e) => dispatch(changeSetting({ name: "showVacatedBookingDockets", value: e.target.checked }))} on={"Show Vacated Bookings Docket"} off={"Hide Vacated Bookings Docket"} width="100%" active={settings["showVacatedBookingDockets"]} />

          <hr className="border" />

          <Row className="align-content-center">
            <div className="fw-bold mb-2 fs-6">Display Preferences</div>
            <>
              {displayPreferenceMap.map((setting, ind) => {
                return (
                  <div key={`display-preference-type-checkbox-${ind}`} className="my-1">
                    <div className="checkbox c-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={setting.isChecked()}
                          onChange={(e) => {
                            setting.onChecked(e.target.checked);
                          }}
                        />
                        {setting.label}
                      </label>
                    </div>
                  </div>
                );
              })}
            </>
          </Row>
          <hr className="border" />

          <Row className="align-content-center">
            <div className="fw-bold mb-2 fs-6">Colour Alerts for Order Time</div>
            <div className="my-1">
              <div className="checkbox c-checkbox mb-3">
                <label>
                  <input type="checkbox" checked={settings.colourAlertsForOrderTime?.enabled} onChange={() => dispatch(toggleColourAlerts())} />
                  Enable colour alerts for order time
                </label>
              </div>

              <Table bordered size="sm" className={`${settings.colourAlertsForOrderTime?.enabled ? "" : "disabled-div"}`}>
                <thead>
                  <tr>
                    <th style={{ width: 120 }}>Colour</th>
                    <th>Time Range (mins)</th>
                  </tr>
                </thead>
                <tbody>
                  {map(orderBy(values(settings.colourAlertsForOrderTime?.alerts), "order", "asc"), ({ colour, _key, order, timeRange }) => (
                    <tr key={`colour-alerts-${_key}`}>
                      <td>
                        <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: colour, marginRight: 8 }} />
                        {startCase(_key)}
                      </td>
                      <td className="d-flex align-items-center gap-2">
                        <input disabled={order === 1} type="number" className="form-control" value={settings.colourAlertsForOrderTime?.alerts?.[_key]?.timeRange?.min ?? 0} onChange={(e) => dispatch(updateColourAlertRange({ _key, field: "min", value: e.target.valueAsNumber }))} />
                        {"max" in timeRange ? (
                          <>
                            <span>-</span>
                            <input type="number" className="form-control" value={settings.colourAlertsForOrderTime?.alerts?.[_key]?.timeRange?.max ?? 0} onChange={(e) => dispatch(updateColourAlertRange({ _key, field: "max", value: e.target.valueAsNumber }))} />
                          </>
                        ) : (
                          <span>+</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Row>
          <hr className="border" />

          <Row className="align-content-center">
            <div className="d-flex align-items-center gap-2 p-2 rounded" >
              <div className="ru-user-avatar">{initials}</div>
              <div className="ru-user-meta">
                <div className="ru-user-name" title={displayName}>
                  {displayName}
                </div>
                <div className="d-flex gap-2 mt-1">
                  <div className="text-dark small">{email}</div>
                  <div className="border-start ps-2 ru-user-role">{role}</div>
                </div>
              </div>
            </div>
          </Row>

          <Row className="row-bordered">
            <Col lg={12} xs={6}>
              <Button type="button" color="primary" className="w-100 align-bottom ru-login-submit p-0" onClick={() => logout()}>
                <LogOut className="icon-logout text-white" size={20} />
                <span className="ms-2" >
                  Logout {loadingLogout && <Spinner size="sm">{""}</Spinner>}
                </span>
              </Button>
            </Col>
          </Row>
        </div>
      </div>
    </aside>
  ) : null;
};

Offsidebar.propTypes = {
  settings: PropTypes.object,
};

export default Offsidebar;
